import { useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Spell } from '../types';
import { PRINT_MODE_STYLES } from '../constants/print';
import { useAutoScaleFont } from '../hooks/useAutoScaleFont';
import './SpellCardExpanded.css';

interface SpellCardExpandedProps {
  spell: Spell;
  isPrintMode?: boolean;
  borderColor?: string;
}

// Format range: "60 feet" -> "60 ft"
function formatRange(range: string): string {
  return range.replace(/\bfeet\b/gi, 'ft');
}

// Format duration with concentration indicator
// "up to 1 minute" with concentration=true -> "1 minute" (C badge shown separately)
function formatDuration(duration: string, concentration: boolean): string {
  let formatted = duration;
  
  // Remove "up to " prefix if present (concentration implies "up to")
  if (concentration) {
    formatted = formatted.replace(/^up to\s+/i, '');
  }
  
  return formatted;
}

// Format components string (keep as-is, just trim whitespace)
function formatComponents(components: string): string {
  return components
    .split(',')
    .map(c => c.trim())
    .join(', ');
}

export function SpellCardExpanded({ spell, isPrintMode, borderColor }: SpellCardExpandedProps) {
  const levelText = `${spell.level}`;
  const formattedRange = formatRange(spell.range);
  const formattedDuration = formatDuration(spell.duration, spell.concentration);
  const formattedComponents = formatComponents(spell.components);

  const cardRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  
  const fontSize = useAutoScaleFont({
    cardRef,
    descriptionRef,
    entityId: spell.id,
    entityType: 'spell',
    deps: [spell.description, spell.material, spell.name, isPrintMode],
  });

  // Build dynamic styles for border color
  const dynamicStyles: React.CSSProperties = {};
  if (borderColor) {
    dynamicStyles.border = `4px solid ${borderColor}`;
  }

  // Compute print mode styles with border override when in print mode
  const printStyles: React.CSSProperties | undefined = isPrintMode
    ? { ...PRINT_MODE_STYLES, ...(borderColor ? { border: `4px solid ${borderColor}` } : {}) }
    : undefined;

  return (
    <div 
      ref={cardRef}
      className={`spell-card-expanded ${isPrintMode ? 'print-mode' : ''}`}
      style={printStyles ?? (borderColor ? dynamicStyles : undefined)}
    >
      <div className="card-header">
        <h2>{spell.name}</h2>
        <span 
          className="level-badge"
          style={borderColor ? { backgroundColor: borderColor } : undefined}
        >
          {levelText}
        </span>
      </div>

      <div className="card-stats">
        <div className="stat-box">
          <h3>CASTING TIME</h3>
          <p>{spell.castingTime}</p>
        </div>
        <div className="stat-box">
          <h3>RANGE</h3>
          <p>{formattedRange}</p>
        </div>
        <div className="stat-box">
          <h3>DURATION</h3>
          <p className="duration-value">
            {spell.concentration && <span className="concentration-badge">C</span>}
            {formattedDuration}
          </p>
        </div>
        <div className="stat-box">
          <h3>COMPONENTS</h3>
          <p>{formattedComponents}</p>
        </div>
      </div>

      <div 
        ref={descriptionRef}
        className="card-description"
        style={{ 
          '--scale-factor': fontSize / 100
        } as React.CSSProperties}
      >
        <Markdown remarkPlugins={[remarkGfm]}>{spell.description}</Markdown>
        {spell.material && (
          <p className="material-component"><em>Material: {spell.material}</em></p>
        )}
      </div>

      <div className="card-footer">
        <span className="school">{spell.school}</span>
        <span className="classes">{spell.classes.join(', ')}</span>
      </div>
    </div>
  );
}
